// SyncContext — orquestra os syncs longos (produtos Tiny + vendas Tiny)
// fora da árvore do Router. Os loops vivem aqui e sobrevivem a navegação
// entre páginas. Só morrem se o user fechar a aba ou der F5.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'

// ── Tipos ─────────────────────────────────────────────────────────────────

interface ProductsResult {
  ok: boolean
  inserted?: number
  stockUpdated?: number
  stockFailed?: number
  suppliersLinked?: number
  pages?: number
  error?: string
}

interface SalesResult {
  ok: boolean
  orders: number
  items: number
  error?: string
}

interface SyncState {
  // Produtos (Tiny V2) — botão "Sincronizar ERP" no header
  productsSyncing: boolean
  productsProgress: { done: number; total: number } | null
  productsLastSync: string | null
  productsResult: ProductsResult | null
  startProductsSync: (testOnly?: boolean) => Promise<ProductsResult>
  refreshProductsLastSync: () => Promise<void>

  // Vendas (Tiny V3) — botão "Sincronizar Vendas" no Dashboard
  salesSyncing: boolean
  salesProgress: { orders: number; items: number } | null
  salesResult: SalesResult | null
  startSalesSync: () => Promise<SalesResult>
}

const SyncContext = createContext<SyncState | null>(null)

// ── Provider ──────────────────────────────────────────────────────────────

const STOCK_CHUNK = 30
const SUPPLIERS_CHUNK = 30
const SALES_MAX_ITERATIONS = 50

interface ProductsPhaseResponse {
  ok: boolean
  phase: 'products'
  inserted?: number
  pages?: number
  totalPages?: number
  stockTotal?: number
  error?: string | null
}

interface StockPhaseResponse {
  ok: boolean
  phase: 'stock'
  processed: number
  updated: number
  failed: number
  nextOffset: number
  done: boolean
  firstError?: string | null
}

interface SuppliersPhaseResponse {
  ok: boolean
  phase: 'suppliers'
  processed: number
  linked: number
  skipped: number
  failed: number
  nextOffset: number
  done: boolean
  firstError?: string | null
}

interface SalesResponse {
  ok: boolean
  done?: boolean
  nextOffset?: number
  processedOrders?: number
  upsertedItems?: number
  error?: string
}

export function SyncProvider({ children }: { children: React.ReactNode }) {
  // Produtos
  const [productsSyncing, setProductsSyncing] = useState(false)
  const [productsProgress, setProductsProgress] = useState<{ done: number; total: number } | null>(null)
  const [productsLastSync, setProductsLastSync] = useState<string | null>(null)
  const [productsResult, setProductsResult] = useState<ProductsResult | null>(null)
  const productsRunningRef = useRef(false)

  // Vendas
  const [salesSyncing, setSalesSyncing] = useState(false)
  const [salesProgress, setSalesProgress] = useState<{ orders: number; items: number } | null>(null)
  const [salesResult, setSalesResult] = useState<SalesResult | null>(null)
  const salesRunningRef = useRef(false)

  // Carrega último sync do Tiny ao logar
  const refreshProductsLastSync = useCallback(async () => {
    const { data } = await supabase.from('companies').select('tiny_last_sync').single()
    if (data) setProductsLastSync(data.tiny_last_sync as string | null)
  }, [])

  useEffect(() => { refreshProductsLastSync() }, [refreshProductsLastSync])

  // ── Sync de produtos ────────────────────────────────────────────────────
  const startProductsSync = useCallback(async (testOnly = false): Promise<ProductsResult> => {
    if (productsRunningRef.current) {
      return { ok: false, error: 'Sincronização já em andamento.' }
    }
    productsRunningRef.current = true
    setProductsSyncing(true)
    setProductsResult(null)
    setProductsProgress(null)

    try {
      // Fase 1: produtos (V3 OAuth2 — puxa sale_price de precos.preco)
      const { data: productsData, error: prodErr } = await supabase.functions.invoke<ProductsPhaseResponse>(
        'tiny-sync-v3',
        { body: { phase: 'products', testOnly } }
      )

      if (prodErr || !productsData?.ok) {
        const r: ProductsResult = {
          ok: false,
          error: prodErr?.message ?? productsData?.error ?? 'Erro na fase produtos',
        }
        setProductsResult(r)
        return r
      }

      if (testOnly) {
        const r: ProductsResult = { ok: true, inserted: productsData.inserted ?? 0, stockUpdated: 0, pages: productsData.pages }
        setProductsResult(r)
        return r
      }

      // Fase 2: estoque em chunks
      const total = productsData.stockTotal ?? 0
      let offset = 0
      let totalUpdated = 0
      let totalFailed = 0
      let firstError: string | null = null

      setProductsProgress({ done: 0, total })

      while (true) {
        const { data: stockData, error: stockErr } = await supabase.functions.invoke<StockPhaseResponse>(
          'tiny-sync-v3',
          { body: { phase: 'stock', offset, limit: STOCK_CHUNK } }
        )

        if (stockErr || !stockData?.ok) {
          firstError = stockErr?.message ?? 'Erro na fase estoque'
          break
        }

        totalUpdated += stockData.updated
        totalFailed += stockData.failed
        if (!firstError && stockData.firstError) firstError = stockData.firstError
        offset = stockData.nextOffset
        setProductsProgress({ done: Math.min(offset, total), total })
        if (stockData.done) break
      }

      // Fase 3: fornecedores em chunks (detalhe GET /produtos/{id} → suppliers + supplier_id).
      // "Tiny manda" + incremental por dataAlteracao; não bloqueia o resultado do sync em caso de erro.
      let suppliersOffset = 0
      let totalLinked = 0
      while (true) {
        const { data: supData, error: supErr } = await supabase.functions.invoke<SuppliersPhaseResponse>(
          'tiny-sync-v3',
          { body: { phase: 'suppliers', offset: suppliersOffset, limit: SUPPLIERS_CHUNK } }
        )
        if (supErr || !supData?.ok) {
          if (!firstError) firstError = supErr?.message ?? supData?.firstError ?? 'Erro na fase fornecedores'
          break
        }
        totalLinked += supData.linked
        if (!firstError && supData.firstError) firstError = supData.firstError
        suppliersOffset = supData.nextOffset
        setProductsProgress({ done: Math.min(suppliersOffset, total), total })
        if (supData.done) break
      }

      // Atualiza tiny_last_sync no estado
      await refreshProductsLastSync()

      const r: ProductsResult = {
        ok: !firstError,
        inserted: productsData.inserted ?? 0,
        stockUpdated: totalUpdated,
        stockFailed: totalFailed,
        suppliersLinked: totalLinked,
        pages: productsData.pages,
        error: firstError ?? undefined,
      }
      setProductsResult(r)
      return r
    } finally {
      setProductsSyncing(false)
      setProductsProgress(null)
      productsRunningRef.current = false
    }
  }, [refreshProductsLastSync])

  // ── Sync de vendas ─────────────────────────────────────────────────────
  const startSalesSync = useCallback(async (): Promise<SalesResult> => {
    if (salesRunningRef.current) {
      return { ok: false, orders: 0, items: 0, error: 'Sincronização de vendas já em andamento.' }
    }
    salesRunningRef.current = true
    setSalesSyncing(true)
    setSalesResult(null)
    setSalesProgress({ orders: 0, items: 0 })

    let offset = 0
    let totalOrders = 0
    let totalItems = 0
    let lastError: string | null = null

    try {
      for (let i = 0; i < SALES_MAX_ITERATIONS; i++) {
        const { data, error: e } = await supabase.functions.invoke<SalesResponse>(
          'tiny-sync-sales-v3',
          { body: { offset } }
        )
        if (e || !data?.ok) { lastError = e?.message ?? data?.error ?? 'Falha na sync'; break }
        totalOrders += data.processedOrders ?? 0
        totalItems  += data.upsertedItems ?? 0
        setSalesProgress({ orders: totalOrders, items: totalItems })
        if (data.done) break
        offset = data.nextOffset ?? offset
      }

      const r: SalesResult = {
        ok: !lastError,
        orders: totalOrders,
        items: totalItems,
        error: lastError ?? undefined,
      }
      setSalesResult(r)
      return r
    } finally {
      setSalesSyncing(false)
      setSalesProgress(null)
      salesRunningRef.current = false
    }
  }, [])

  return (
    <SyncContext.Provider
      value={{
        productsSyncing,
        productsProgress,
        productsLastSync,
        productsResult,
        startProductsSync,
        refreshProductsLastSync,
        salesSyncing,
        salesProgress,
        salesResult,
        startSalesSync,
      }}
    >
      {children}
    </SyncContext.Provider>
  )
}

export function useSync(): SyncState {
  const ctx = useContext(SyncContext)
  if (!ctx) throw new Error('useSync deve ser usado dentro de SyncProvider')
  return ctx
}
